import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { cloneDeep } from 'es-toolkit';

import { Box, List, Paragraph, Toast, useToast } from '@zillow/constellation-10';

import { PhotoDetailsModal } from '@zg-rentals/maintenance-utils';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyFloorPlanPhotoV2WebApiHandler_caption,
  useRentalManagerUserPropertyFloorPlanPhotoV2WebApiHandler_delete,
  useRentalManagerUserPropertyMultiplexWebApiHandler_details,
  useRentalManagerUserPropertyPhotoV2WebApiHandler_caption,
  useRentalManagerUserPropertyPhotoV2WebApiHandler_delete,
  useRentalManagerUserPropertyPhotoV2WebApiHandler_rank,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import {
  useIsOnReviewStep,
  useMultiplexBuildingSessionId,
  useMultiplexDetails,
  useMultiplexListingFlow,
  useMultiplexPhotoCaption,
  useMultiplexPhotoDelete,
  useMultiplexPhotoRank,
} from '../../../../../hooks/queries/multiplex';
import { useGetListingDetails } from '../../../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../../../hooks/usePropertyEditMutation';
import { getPhotoUrls } from '../../../../../utils/propertiesPageUtils';

import DiscriminatoryContentErrorAlert from '../../../../../components/PhotoGallery/components/DiscriminatoryContentErrorAlert';
import SortableItem from '../../../../../components/PhotoGallery/components/SortableItem';

import { useAuth } from '../../../../AuthProvider';
import PhotoUploadDropzone from './PhotoUpload';

import type { DragEndEvent } from '@dnd-kit/core';
import type { Dispatch, SetStateAction } from 'react';
import type { PhotoProps } from '@zg-rentals/maintenance-utils';

type PhotoContext =
  | { type: 'property'; listingAlias: string }
  | { type: 'floorPlan'; floorPlanId: string; floorPlanIndex: number };

type Props = {
  photoContext: PhotoContext;
  uploadingCount: number;
  setUploadingCount: Dispatch<SetStateAction<number>>;
  /** Passed from PhotoUploadFormField so parent can sync multiplex/listing photos and optimistic uploads. */
  photos?: Array<PhotoProps>;
  setPhotos?: Dispatch<SetStateAction<Array<PhotoProps>>>;
  /** Forwards successful uploads to parent state (floor-plan usage omits this). */
  onUploadSuccess?: (uploadedPhotos: Array<PhotoProps>) => void;
  /** Clears pending-upload tracking when a photo is removed (PhotoUploadFormField only). */
  onPhotoDeleted?: (photoId: string) => void;
};

const DEFAULT_CAPTION_SAVE_ERROR_MESSAGE = 'Caption couldn’t be saved. Try again.';

type CaptionSaveUpdateContext = {
  photos: Array<PhotoProps>;
  photoId: string;
  caption: string;
  setPhotos: Dispatch<SetStateAction<Array<PhotoProps>>>;
  discriminatoryContentErrors: Array<{ photoId: string; caption: string }>;
  setDiscriminatoryContentErrors: Dispatch<SetStateAction<Array<{ photoId: string; caption: string }>>>;
};

const applyCaptionSaveError = (context: CaptionSaveUpdateContext, errorMessage?: string): void => {
  const { photos, photoId, caption, setPhotos, discriminatoryContentErrors, setDiscriminatoryContentErrors } = context;
  const displayError = errorMessage || DEFAULT_CAPTION_SAVE_ERROR_MESSAGE;
  const copyPhotos = cloneDeep(photos);
  const indexToUpdate = copyPhotos.findIndex((photo) => photo.id === photoId);

  if (indexToUpdate === -1) {
    return;
  }

  copyPhotos[indexToUpdate].error = displayError;
  copyPhotos[indexToUpdate].caption = caption;
  setPhotos(copyPhotos);

  if (errorMessage?.includes('Good Neighbor Policy')) {
    setDiscriminatoryContentErrors([...discriminatoryContentErrors, { photoId, caption }]);
  }
};

const applyCaptionSaveSuccess = (
  context: Pick<CaptionSaveUpdateContext, 'photos' | 'photoId' | 'caption' | 'setPhotos'>,
): void => {
  const { photos, photoId, caption, setPhotos } = context;
  const copyPhotos = cloneDeep(photos);
  const indexToUpdate = copyPhotos.findIndex((photo) => photo.id === photoId);

  if (indexToUpdate === -1) {
    return;
  }

  copyPhotos[indexToUpdate].caption = caption;
  copyPhotos[indexToUpdate].error = undefined;
  setPhotos(copyPhotos);
};

const PhotoGallery = ({
  photoContext,
  uploadingCount,
  setUploadingCount,
  photos: photosProp,
  setPhotos: setPhotosProp,
  onUploadSuccess,
  onPhotoDeleted,
}: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  const listingAlias =
    photoContext.type === 'property' ? photoContext.listingAlias : (router.query.listingAlias as string);

  const isOnReviewStep = useIsOnReviewStep();

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { data: multiplexData } = useMultiplexDetails({
    enabled: !isOnReviewStep,
  });

  const [internalPhotos, setInternalPhotos] = useState<Array<PhotoProps>>([]);
  const photos = photosProp ?? internalPhotos;
  const setPhotos = setPhotosProp ?? setInternalPhotos;
  /** PhotoUploadFormField supplies photos/setPhotos; floor plans use internal state only. */
  const isControlled = photosProp != null && setPhotosProp != null;
  const [clickedPhotoIdx, setClickedPhotoIdx] = useState<number | undefined | null>();
  const [discriminatoryContentErrors, setDiscriminatoryContentErrors] = useState<
    Array<{ photoId: string; caption: string }>
  >([]);

  const isMultifamily = listingDetails?.isMultifamily ?? false;
  const isFeed = listingDetails?.isFeed ?? false;
  const isArchived = listingDetails?.isArchived ?? false;
  const isUpdateListingRestricted = listingDetails?.restrictions?.isUpdateListingRestricted ?? false;
  const isSelfServeOnboardingInProgress =
    listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress ?? false;
  const isSSOBUpdateRestricted = listingDetails?.selfServeOnboardingStatus?.isUpdateRestricted ?? false;

  const canUpdateListing = user?.features?.canUpdateListing ?? false;
  const isRunningInProd = process.env.NODE_ENV === 'production';

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();
  /**
   * Multiplex building-session APIs for sort/delete/caption on property photos during the wizard.
   * On review (`?step=review`), use per-listing photo APIs instead.
   */
  const saveViaMultiplex =
    photoContext.type === 'property' && isOnReviewStep === false && Boolean(isInMultiplexListingFlow);

  const { mutateAsync: multiplexDeletePhotoAsync } = useMultiplexPhotoDelete({
    meta: {
      invalidates: ['getMultiplexUnits', 'getPropertyTableUnits', 'getGroupableProperties'],
      context: {
        api: 'useMultiplexPhotoDelete',
        details: 'PhotoGallery',
      },
    },
  });

  const { mutateAsync: multiplexSortPhotoAsync } = useMultiplexPhotoRank({
    meta: {
      invalidates: ['getMultiplexUnits', 'getPropertyTableUnits', 'getGroupableProperties'],
      context: {
        api: 'useMultiplexPhotoRank',
        details: 'PhotoGallery',
      },
    },
  });

  const { mutateAsync: multiplexPhotoCaptionAsync } = useMultiplexPhotoCaption({
    meta: {
      invalidates: ['getMultiplexUnits', 'getPropertyTableUnits', 'getGroupableProperties'],
      context: {
        api: 'useMultiplexPhotoCaption',
        details: 'PhotoGallery',
      },
    },
  });

  const isDragDisabled = photoContext.type === 'floorPlan' || isFeed;

  const { mutateAsync: sortPhotos } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyPhotoV2WebApiHandler_rank,
    skipToast: true,
    meta: {
      invalidates: ['getMultiplexUnits', 'getPropertyTableUnits', 'getGroupableProperties'],
    },
  });

  const { mutateAsync: deletePropertyPhoto } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyPhotoV2WebApiHandler_delete,
    successToastMessage: 'Photo deleted',
    meta: {
      invalidates: ['getMultiplexUnits', 'getPropertyTableUnits', 'getGroupableProperties'],
    },
  });

  const { mutateAsync: deleteFloorPlanPhoto } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanPhotoV2WebApiHandler_delete,
    successToastMessage: 'Photo deleted',
  });

  const { mutateAsync: updatePropertyPhotoCaption } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyPhotoV2WebApiHandler_caption,
    skipToast: true,
  });

  const { mutateAsync: updateFloorPlanPhotoCaption } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanPhotoV2WebApiHandler_caption,
    skipToast: true,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // hack to disable drag and drop till the photo is moved greater than 3000px which is not common on desktop
        distance: isDragDisabled ? 3000 : 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: isDragDisabled ? 3000 : 5,
      },
    }),
  );

  /** Parent (PhotoUploadFormField) owns photo sync when controlled; floor plans load from listing here. */
  useEffect(() => {
    if (isControlled) {
      return;
    }

    const sourcePhotos =
      photoContext.type === 'property'
        ? isInMultiplexListingFlow && !isOnReviewStep
          ? multiplexData?.propertyPhotos
          : listingDetails?.propertyPhotos
        : listingDetails?.floorPlans?.[photoContext.floorPlanIndex]?.floorPlanPhotos;

    if (sourcePhotos != null) {
      setPhotos(sourcePhotos);
    }
  }, [isControlled, photoContext, listingDetails?.propertyPhotos, listingDetails?.floorPlans, setPhotos]);

  const handleDragEnd = async (event: DragEndEvent) => {
    if (uploadingCount) {
      return;
    }

    const { active, over } = event;

    // `over` is null when the item is dropped outside a valid sortable target; only reorder real item-to-item drops.
    if (active.id !== over?.id && over) {
      setPhotos((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
          return items;
        }

        const sortedArray: Array<PhotoProps> = arrayMove(items, oldIndex, newIndex);
        const sortedArraysWithUpdatedRank = sortedArray.map((photo, index) => ({ ...photo, rank: index + 1 }));

        const ranksPayload = sortedArraysWithUpdatedRank.map((p) => ({ id: p.id, rank: String(p.rank) }));
        const mutationFunc = saveViaMultiplex ? multiplexSortPhotoAsync : sortPhotos;
        let previousMultiplexData: typeof multiplexData;

        if (saveViaMultiplex) {
          // Keep the active multiplex details cache aligned with the optimistic dropped order.
          queryClient.setQueryData(
            useRentalManagerUserPropertyMultiplexWebApiHandler_details.getKey({
              multiplexBuildingSessionId: multiplexBuildingSessionId as string,
            }),
            (oldData: typeof multiplexData) => {
              previousMultiplexData = oldData;
              return oldData ? { ...oldData, propertyPhotos: sortedArraysWithUpdatedRank } : oldData;
            },
          );
        }

        const reorderPromise = saveViaMultiplex
          ? (mutationFunc as typeof multiplexSortPhotoAsync)({
              postData: { multiplexBuildingSessionId: multiplexBuildingSessionId as string, ranks: ranksPayload },
            })
          : (mutationFunc as typeof sortPhotos)({
              postData: { listingAlias, ranks: ranksPayload },
            });

        void reorderPromise
          .then(() => {
            if (saveViaMultiplex) {
              void queryClient.invalidateQueries({
                queryKey: useRentalManagerUserPropertyMultiplexWebApiHandler_details.getKey({
                  multiplexBuildingSessionId: multiplexBuildingSessionId as string,
                }),
                refetchType: 'inactive',
              });
            }
          })
          .catch(() => {
            if (saveViaMultiplex && previousMultiplexData) {
              queryClient.setQueryData(
                useRentalManagerUserPropertyMultiplexWebApiHandler_details.getKey({
                  multiplexBuildingSessionId: multiplexBuildingSessionId as string,
                }),
                previousMultiplexData,
              );
            }

            enqueueToast(<Toast tone="critical">Photos couldn’t be reordered. Refresh and try again.</Toast>);
          });

        return sortedArraysWithUpdatedRank;
      });
    }
  };

  const handleDeletePhoto = async ({
    photoIdAtSlidePosition,
    onComplete,
  }: {
    photoIdAtSlidePosition: string;
    onComplete: () => void;
  }) => {
    try {
      if (saveViaMultiplex) {
        await multiplexDeletePhotoAsync({
          postData: {
            multiplexBuildingSessionId: multiplexBuildingSessionId as string,
            photoId: photoIdAtSlidePosition,
          },
        });
      } else if (photoContext.type === 'property') {
        await deletePropertyPhoto({ postData: { photoId: photoIdAtSlidePosition } });
      } else {
        await deleteFloorPlanPhoto({ postData: { photoId: photoIdAtSlidePosition } });
      }

      setPhotos((prev) => prev.filter((photo) => photo.id !== photoIdAtSlidePosition));
      onPhotoDeleted?.(photoIdAtSlidePosition);

      setClickedPhotoIdx(null);

      if (!isMultifamily) {
        enqueueToast(<Toast tone="success">Photo deleted</Toast>);
      }
    } catch (err) {
      enqueueToast(
        <Toast tone="critical">
          {err.message || 'There was an error with deleting the photo, refresh the page to try again'}
        </Toast>,
      );
    } finally {
      setTimeout(() => {
        onComplete();
      }, 0);
    }
  };

  const handleCaptionUpdate = async ({
    photoId,
    caption,
    onComplete,
  }: {
    photoId: string;
    caption: string;
    onComplete: () => void;
  }) => {
    const captionSaveContext: CaptionSaveUpdateContext = {
      photos,
      photoId,
      caption,
      setPhotos,
      discriminatoryContentErrors,
      setDiscriminatoryContentErrors,
    };

    try {
      if (saveViaMultiplex) {
        const result = await multiplexPhotoCaptionAsync({
          postData: {
            multiplexBuildingSessionId: multiplexBuildingSessionId as string,
            photoId,
            caption,
          },
        });

        if (!result.success) {
          applyCaptionSaveError(captionSaveContext, result.errorMessage);
        } else {
          applyCaptionSaveSuccess(captionSaveContext);
        }
      } else if (photoContext.type === 'property') {
        await updatePropertyPhotoCaption({ postData: { photoId, caption } });
      } else {
        await updateFloorPlanPhotoCaption({ postData: { photoId, caption } });
      }
    } catch (err) {
      const message = (err as { error?: { message?: string } })?.error?.message;
      if (message) {
        applyCaptionSaveError(captionSaveContext, message);
      }
    } finally {
      setTimeout(() => {
        onComplete();
      }, 0);
    }
  };

  const handleClearError = ({ photoAtSlidePosition }: { photoAtSlidePosition: PhotoProps }) => {
    if (photoAtSlidePosition.error) {
      const copyPhotos = cloneDeep(photos);
      const indexToUpdate = copyPhotos.findIndex((copyPhoto) => copyPhoto.id === photoAtSlidePosition.id);

      copyPhotos[indexToUpdate].error = undefined;
      setPhotos(copyPhotos);

      const copyDiscriminatoryContent = cloneDeep(discriminatoryContentErrors);
      const errorIndexToUpdate = copyDiscriminatoryContent.findIndex(
        (error) => error.photoId === photoAtSlidePosition.id,
      );
      copyDiscriminatoryContent.splice(errorIndexToUpdate, 1);

      setDiscriminatoryContentErrors(copyDiscriminatoryContent);
    }
  };

  if (!photos) {
    return null;
  }

  return (
    <div data-testid="PhotoGallery">
      {!isDragDisabled && <Paragraph css={{ marginBottom: '400' }}>Drag and drop to reorder</Paragraph>}

      {discriminatoryContentErrors.length > 0 && (
        <DiscriminatoryContentErrorAlert errors={discriminatoryContentErrors} />
      )}

      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          columnGap: '400',
          rowGap: '400',
        }}
      >
        {!isFeed && (
          <Box css={{ gridColumnStart: '1', gridColumnEnd: 'span 1' }}>
            <PhotoUploadDropzone
              photoContext={photoContext}
              isSmallDropzone={true}
              setUploadingCount={setUploadingCount}
              uploadingCount={uploadingCount}
              onUploadSuccess={onUploadSuccess}
            />
          </Box>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={photos} strategy={rectSortingStrategy}>
            <List.Root
              appearance="number"
              aria-label="property photos"
              css={{ listStyleType: 'none', paddingInlineStart: 0, display: 'contents' }}
            >
              {photos.map((photo: PhotoProps, idx: number) => (
                <SortableItem
                  key={photo.id}
                  errors={discriminatoryContentErrors}
                  handlePhotoClick={() => setClickedPhotoIdx(idx)}
                  photo={photo}
                  index={idx}
                  isDisabled={!!uploadingCount}
                  isRunningInProd={isRunningInProd}
                />
              ))}
              {uploadingCount > 0 &&
                Array.from({ length: uploadingCount }).map((_, idx) => (
                  <SortableItem
                    key={`placeholder-photo-${uploadingCount}-${idx}`}
                    isUploading={true}
                    photo={{ id: '' }}
                    index={photos.length + idx}
                    isDisabled={true}
                    isRunningInProd={isRunningInProd}
                  />
                ))}
            </List.Root>
          </SortableContext>
        </DndContext>
      </Box>

      {!!(clickedPhotoIdx || clickedPhotoIdx === 0) && (
        <PhotoDetailsModal
          additionalDisabledChecks={
            Boolean(uploadingCount > 0) ||
            isFeed ||
            !canUpdateListing ||
            isArchived ||
            isUpdateListingRestricted ||
            (isSelfServeOnboardingInProgress && isSSOBUpdateRestricted)
          }
          idx={clickedPhotoIdx}
          onCaptionUpdate={handleCaptionUpdate}
          onClearError={handleClearError}
          onClose={() => setClickedPhotoIdx(null)}
          onDeletePhoto={handleDeletePhoto}
          photos={photos}
          getPhotoUrls={getPhotoUrls}
          isRunningInProd={isRunningInProd}
        />
      )}
    </div>
  );
};

export default PhotoGallery;
